import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Home, Ruler, MessageSquare, Clock, LogOut, RefreshCw, Inbox, Loader2, Bell, BellOff, Volume2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <span>
        <span className="text-slate-400">{label}: </span>
        <span className="text-slate-700">{value}</span>
      </span>
    </div>
  ) : null;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const { token, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [muted, setMuted] = useState(() => localStorage.getItem("wisp_chime_muted") === "1");
  const mutedRef = useRef(muted);
  const audioCtxRef = useRef(null);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const getCtx = () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!audioCtxRef.current) audioCtxRef.current = new AC();
    if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
    return audioCtxRef.current;
  };

  const playChime = () => {
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    [523.25, 659.25, 783.99].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t = now + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
      osc.start(t);
      osc.stop(t + 0.66);
    });
  };

  // Chime at noon (Eastern), then hourly through 4pm, Mon–Fri, while unresponded leads remain.
  const maybeChime = (unrespondedCount) => {
    if (mutedRef.current || unrespondedCount <= 0) return;
    const est = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Toronto" }));
    const day = est.getDay();
    const hour = est.getHours();
    if (day < 1 || day > 5) return;
    if (hour < 12 || hour > 16) return;
    const slot = `${est.toDateString()}-${hour}`;
    if (localStorage.getItem("wisp_last_chime_slot") === slot) return;
    localStorage.setItem("wisp_last_chime_slot", slot);
    playChime();
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(data);
      maybeChime(data.filter((l) => !l.responded).length);
    } catch (err) {
      if (!silent) setError("Couldn't load leads. Please try again.");
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => load(true), 60000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const warm = () => getCtx();
    window.addEventListener("pointerdown", warm);
    window.addEventListener("keydown", warm);
    return () => {
      window.removeEventListener("pointerdown", warm);
      window.removeEventListener("keydown", warm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMuted = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem("wisp_chime_muted", next ? "1" : "0");
    if (!next) getCtx();
  };

  const toggleResponded = async (lead) => {
    const next = !lead.responded;
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, responded: next } : l)));
    try {
      await axios.patch(
        `${API}/leads/${lead.id}`,
        { responded: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, responded: !next } : l)));
    }
  };

  const newCount = leads.filter((l) => !l.responded).length;

  useEffect(() => {
    const base = "Wisp — Leads Dashboard";
    if (newCount <= 0) {
      document.title = base;
      return;
    }
    let on = false;
    const flash = () => {
      on = !on;
      document.title = on ? `(${newCount}) Lead${newCount > 1 ? "s" : ""} waiting` : base;
    };
    flash();
    const id = setInterval(flash, 1200);
    return () => {
      clearInterval(id);
      document.title = base;
    };
  }, [newCount]);

  return (
    <div className="min-h-screen bg-muted">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Logo variant="mark" imgClassName="h-10 w-auto" />
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMuted}
              data-testid="chime-toggle"
              title={muted ? "Sound alerts are off" : "Sound alerts are on"}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${muted ? "border-slate-300 text-slate-500 hover:border-slate-400" : "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"}`}
            >
              {muted ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
              {muted ? "Alerts off" : "Alerts on"}
            </button>
            <button
              onClick={() => { getCtx(); playChime(); }}
              data-testid="chime-test"
              title="Play a test chime"
              className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-primary hover:text-primary"
            >
              <Volume2 className="h-4 w-4" /> Test
            </button>
            <button
              onClick={() => load()}
              data-testid="dashboard-refresh"
              className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-primary hover:text-primary"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="dashboard-logout"
              className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-medium text-slate-900">Enquiries</h1>
          <div className="flex items-center gap-2">
            {newCount > 0 && (
              <span data-testid="new-count" className="rounded-full bg-accent px-3 py-1 text-sm font-semibold text-white">
                {newCount} new
              </span>
            )}
            <span data-testid="leads-count" className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              {leads.length} total
            </span>
          </div>
        </div>

        {newCount > 0 && (
          <div
            data-testid="leads-waiting-banner"
            className="mt-4 flex items-center gap-3 rounded-2xl border border-accent/40 bg-accent/10 px-5 py-4"
          >
            <span className="relative flex h-3 w-3 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-accent" />
            </span>
            <p className="text-sm font-semibold text-slate-800">
              You have {newCount} {newCount === 1 ? "lead" : "leads"} waiting for a reply — please respond and mark {newCount === 1 ? "it" : "them"} as responded.
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : error ? (
          <p className="mt-10 text-center text-destructive">{error}</p>
        ) : leads.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-3xl border border-dashed border-slate-300 py-20 text-center">
            <Inbox className="h-10 w-10 text-slate-300" />
            <p className="mt-4 font-heading text-lg text-slate-600">No enquiries yet</p>
            <p className="mt-1 text-sm text-slate-400">New form submissions will appear here.</p>
          </div>
        ) : (
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {leads.map((lead) => (
              <article
                key={lead.id}
                data-testid="lead-card"
                className={`rounded-2xl border bg-white p-6 shadow-sm transition-colors ${lead.responded ? "border-slate-200 opacity-75" : "border-primary/40 ring-1 ring-primary/20"}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-heading text-lg font-semibold text-slate-900">{lead.name}</h2>
                      {!lead.responded && (
                        <span data-testid="new-badge" className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">
                      {fmtDate(lead.created_at)}{lead.source ? ` · ${lead.source}` : ""}
                    </p>
                  </div>
                  <a
                    href={`tel:${lead.phone}`}
                    className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
                  >
                    <Phone className="h-3.5 w-3.5" /> Call
                  </a>
                </div>
                <div className="mt-4 space-y-2">
                  <Field icon={Phone} label="Phone" value={lead.phone} />
                  <Field icon={Mail} label="Email" value={lead.email} />
                  <Field icon={MapPin} label="Area" value={lead.location} />
                  <Field icon={Home} label="Property" value={lead.property_type} />
                  <Field icon={Ruler} label="Size" value={lead.approx_size} />
                  <Field icon={MessageSquare} label="Preferred contact" value={lead.preferred_contact} />
                  <Field icon={Clock} label="Availability" value={lead.availability} />
                </div>
                {lead.cleaning_needs && (
                  <div className="mt-4 rounded-xl bg-muted p-3 text-sm text-slate-700">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">About their home</p>
                    {lead.cleaning_needs}
                  </div>
                )}
                {lead.photos && lead.photos.length > 0 && (
                  <div className="mt-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Photos</p>
                    <div className="flex flex-wrap gap-2">
                      {lead.photos.map((p) => (
                        <a key={p.id} href={`${API}/files/${p.path}`} target="_blank" rel="noopener noreferrer">
                          <img
                            src={`${API}/files/${p.path}`}
                            alt={p.filename}
                            className="h-20 w-20 rounded-lg border border-slate-200 object-cover transition-transform hover:scale-105"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <label
                  data-testid="responded-toggle"
                  className="mt-5 flex cursor-pointer items-center gap-2 border-t border-slate-100 pt-4 text-sm font-medium text-slate-600"
                >
                  <input
                    type="checkbox"
                    checked={!!lead.responded}
                    onChange={() => toggleResponded(lead)}
                    className="h-4 w-4 accent-[hsl(173,58%,39%)]"
                  />
                  {lead.responded ? "Responded ✓" : "Mark as responded"}
                </label>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
